import {
  Button,
  Checkbox,
  DeleteIcon,
  ExternalLinkIcon,
  Input,
  LogOutIcon,
  Modal,
  Select,
  UpdateIcon,
} from "@bp/ui-components";
import type {
  SelectOptionType,
  SelectOptionsType,
} from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { type ReactNode, useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation } from "urql";
import { type FragmentType, getFragmentData, graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "./ClientFormModal.module.scss";

// Colocated fragment: the fields the edit form seeds its state from. Spread by
// AuthClientDetailPage's query so it can hand the masked client to this modal.
export const ClientFormModal_AuthClient = graphql(`
  fragment ClientFormModal_AuthClient on AuthClient {
    clientId
    clientName
    clientSecret
    tokenEndpointAuthMethod
    idTokenSignedResponseAlg
    grantTypes
    responseTypes
    scope
    redirectUris
    postLogoutRedirectUris
    logoUri
    tosUri
    policyUri
    tksExtensionPkceOptional
    tksExtensionConsentOptional
    tksExtensionLicenseOptional
  }
`);

const CreateAuthClientsMutation = graphql(`
  mutation ClientFormModal_CreateAuthClients($input: [AuthClientCreateInput!]!) {
    createAuthClients(input: $input) {
      authClients {
        clientId
      }
    }
  }
`);

const UpdateAuthClientsMutation = graphql(`
  mutation ClientFormModal_UpdateAuthClients($clientId: ID!, $update: AuthClientUpdateInput!) {
    updateAuthClients(where: { clientId: $clientId }, update: $update) {
      authClients {
        clientId
      }
    }
  }
`);

type Props =
  | { mode: "create"; client?: undefined; onClose: () => void; onSaved: (clientId: string) => void }
  | {
      mode: "edit";
      client: FragmentType<typeof ClientFormModal_AuthClient>;
      onClose: () => void;
      onSaved: (clientId: string) => void;
    };

// scope / auth-method catalogs mirror the prototype's data.jsx
const SCOPE_OPTIONS = [
  "openid",
  "offline_access",
  "email",
  "profile",
  "roles",
  "user",
  "other_profiles",
  "child_profiles",
  "cloud",
  "omni_organization",
];
const SCOPE_INFO: Record<string, { label: string; sensitive?: boolean }> = {
  openid: { label: "Identitäts-Token (sub)" },
  profile: { label: "Name, Profilangaben" },
  email: { label: "E-Mail-Adresse" },
  offline_access: { label: "Refresh-Token (dauerhafter Login)", sensitive: true },
  roles: { label: "Rollen-Claims" },
  user: { label: "Konto-Stammdaten" },
  other_profiles: { label: "Verknüpfte Profile (z. B. Kinder)", sensitive: true },
  child_profiles: { label: "Profile betreuter Kinder", sensitive: true },
  cloud: { label: "Cloud-Speicher-Zugriff", sensitive: true },
  omni_organization: { label: "Organisationsübergreifender Zugriff", sensitive: true },
};
const GRANT_TYPE_OPTIONS = ["authorization_code", "refresh_token", "client_credentials"];
const RESPONSE_TYPE_OPTIONS = ["code", "id_token"];
const AUTH_METHOD_OPTIONS: SelectOptionType[] = [
  { value: "none", label: "none (Public Client)" },
  { value: "client_secret_basic", label: "client_secret_basic" },
  { value: "client_secret_post", label: "client_secret_post" },
];

const genSecret = () => {
  const bytes = new Uint8Array(24);
  (globalThis.crypto ?? ({} as Crypto)).getRandomValues?.(bytes);
  return `cs_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
};

/* multi-select option sets (DS Select, isMulti) */
const GRANT_TYPE_SELECT: SelectOptionType[] = GRANT_TYPE_OPTIONS.map((v) => ({
  value: v,
  label: v,
}));
const RESPONSE_TYPE_SELECT: SelectOptionType[] = RESPONSE_TYPE_OPTIONS.map((v) => ({
  value: v,
  label: v,
}));
const SCOPE_SELECT: SelectOptionType[] = SCOPE_OPTIONS.map((v) => ({
  value: v,
  label: SCOPE_INFO[v] ? `${v} — ${SCOPE_INFO[v].label}` : v,
}));

/** current string[] state → the matching option objects DS Select expects. */
const toSelected = (opts: SelectOptionType[], values: string[]): SelectOptionType[] =>
  opts.filter((o) => values.includes(o.value as string));
/** DS Select multi onChange payload → string[] of the selected values. */
const toValues = (e: SelectOptionsType): string[] =>
  (Array.isArray(e) ? e : []).map((o) => String((o as SelectOptionType).value));

/* multi-row URI editor */
function UriEditor({
  label,
  hint,
  icon,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  icon: ReactNode;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) {
  const rows = value.length ? value : [""];
  const setAt = (i: number, v: string) => {
    const next = [...rows];
    next[i] = v;
    onChange(next);
  };
  const removeAt = (i: number) => {
    const next = rows.filter((_, j) => j !== i);
    onChange(next.length ? next : [""]);
  };
  return (
    <div className={css.fld}>
      <label className={css.label}>{label}</label>
      {hint && <span className={css.hint}>{hint}</span>}
      <div className={css.stackSm}>
        {rows.map((u, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional URI rows
          <div key={i} className={css.uriEditorRow}>
            {icon}
            <div className={css.uriEditorInput}>
              <Input
                name={`uri-${i}`}
                dense
                value={u}
                placeholder={placeholder}
                className={css.monoInput}
                onChange={(e) => setAt(i, e.target.value)}
              />
            </div>
            <Button
              hierarchy="tertiary"
              height="s"
              icon={<DeleteIcon />}
              ariaLabel="Entfernen"
              disabled={rows.length === 1 && !u}
              onClick={() => removeAt(i)}
            />
          </div>
        ))}
      </div>
      <Button hierarchy="tertiary" height="s" onClick={() => onChange([...rows, ""])}>
        Eintrag hinzufügen
      </Button>
    </div>
  );
}

export function ClientFormModal(props: Props) {
  const { mode, onClose, onSaved } = props;
  const client = getFragmentData(
    ClientFormModal_AuthClient,
    mode === "edit" ? props.client : undefined,
  );
  const isEdit = mode === "edit";

  const [clientName, setClientName] = useState(client?.clientName ?? "");
  const [clientId, setClientId] = useState(client?.clientId ?? "");
  const [authMethod, setAuthMethod] = useState(client?.tokenEndpointAuthMethod ?? "none");
  const [clientSecret, setClientSecret] = useState(client?.clientSecret ?? "");
  const [idTokenAlg, setIdTokenAlg] = useState(client?.idTokenSignedResponseAlg ?? "RS256");
  const [grantTypes, setGrantTypes] = useState<string[]>(
    client?.grantTypes?.length ? [...client.grantTypes] : ["authorization_code"],
  );
  const [responseTypes, setResponseTypes] = useState<string[]>(
    client?.responseTypes?.length ? [...client.responseTypes] : ["code"],
  );
  const [scopes, setScopes] = useState<string[]>(
    client ? (client.scope ?? "").split(/\s+/).filter(Boolean) : ["openid"],
  );
  const [redirectUris, setRedirectUris] = useState<string[]>(
    client?.redirectUris?.length ? [...client.redirectUris] : [""],
  );
  const [postLogout, setPostLogout] = useState<string[]>([
    ...(client?.postLogoutRedirectUris ?? []),
  ]);
  const [logoUri, setLogoUri] = useState(client?.logoUri ?? "");
  const [tosUri, setTosUri] = useState(client?.tosUri ?? "");
  const [policyUri, setPolicyUri] = useState(client?.policyUri ?? "");
  const [pkceOptional, setPkceOptional] = useState(Boolean(client?.tksExtensionPkceOptional));
  const [consentOptional, setConsentOptional] = useState(
    Boolean(client?.tksExtensionConsentOptional),
  );
  const [licenseOptional, setLicenseOptional] = useState(
    Boolean(client?.tksExtensionLicenseOptional),
  );
  const [busy, setBusy] = useState(false);

  const [, createClient] = useMutation(CreateAuthClientsMutation);
  const [, updateClient] = useMutation(UpdateAuthClientsMutation);

  const isPublic = authMethod === "none";

  const valid =
    Boolean(clientName.trim()) &&
    (isEdit || Boolean(clientId.trim())) &&
    // backend rejects empty grant/response arrays — require at least one each
    grantTypes.length > 0 &&
    responseTypes.length > 0;

  const save = async () => {
    setBusy(true);
    const redirect = redirectUris.map((s) => s.trim()).filter(Boolean);
    const postLogoutList = postLogout.map((s) => s.trim()).filter(Boolean);
    const scope = scopes.join(" ").trim();
    // The backend rejects empty arrays / empty scope — send undefined (omit) instead.
    const shared = {
      clientName: clientName.trim(),
      tokenEndpointAuthMethod: authMethod,
      ...(!isPublic && clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
      ...(redirect.length ? { redirectUris: redirect } : {}),
      ...(postLogoutList.length ? { postLogoutRedirectUris: postLogoutList } : {}),
      ...(scope ? { scope } : {}),
      ...(idTokenAlg.trim() ? { idTokenSignedResponseAlg: idTokenAlg.trim() } : {}),
      ...(logoUri.trim() ? { logoUri: logoUri.trim() } : {}),
      ...(tosUri.trim() ? { tosUri: tosUri.trim() } : {}),
      ...(policyUri.trim() ? { policyUri: policyUri.trim() } : {}),
      grantTypes,
      responseTypes,
      tksExtensionPkceOptional: pkceOptional,
      tksExtensionConsentOptional: consentOptional,
      tksExtensionLicenseOptional: licenseOptional,
    };
    try {
      if (mode === "create") {
        const res = await createClient({ input: [{ clientId: clientId.trim(), ...shared }] });
        if (res.error) {
          throw res.error;
        }
        showSuccessToast(`OAuth-Client „${clientName}" angelegt`);
        onSaved(clientId.trim());
      } else {
        const res = await updateClient({ clientId: client?.clientId ?? "", update: shared });
        if (res.error) {
          throw res.error;
        }
        showSuccessToast(`OAuth-Client „${clientName}" gespeichert`);
        onSaved(client?.clientId ?? "");
      }
    } catch (error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error });
      setBusy(false);
    }
  };

  const Toggle = ({
    checked,
    onToggle,
    title,
    sub,
  }: {
    checked: boolean;
    onToggle: (v: boolean) => void;
    title: string;
    sub: string;
  }) => (
    <div className={css.toggleRow}>
      <Checkbox name={title} checked={checked} onChange={(e) => onToggle(e.target.checked)} />
      <span>
        <span className={css.toggleTitle}>{title}</span>
        <span className={css.toggleSub}>{sub}</span>
      </span>
    </div>
  );

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={isEdit ? "OAuth-Client bearbeiten" : "OAuth-Client erstellen"}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>
            {isEdit
              ? "ClientId ist nach dem Anlegen unveränderlich."
              : isPublic
                ? "Public Client (token_endpoint_auth_method = none)."
                : "Confidential Client mit Secret."}
          </span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={!valid || busy} onClick={() => void save()}>
              {isEdit ? "Speichern" : "Erstellen"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="clientName"
          label="Client Name"
          placeholder="z. B. TK-Zeugnis Web"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />

        <div className={css.grid2}>
          <Input
            name="clientId"
            label="ClientId"
            value={clientId}
            disabled={isEdit}
            className={css.monoInput}
            onChange={(e) => setClientId(e.target.value)}
          />
          <Input
            name="clientSecret"
            label="Client Secret"
            value={isPublic ? "" : clientSecret}
            disabled={isPublic}
            placeholder={
              isPublic
                ? "Public Client — kein Secret"
                : isEdit
                  ? "Unverändert — leer lassen behält das bestehende Secret"
                  : ""
            }
            className={css.monoInput}
            suffix={
              <Button
                hierarchy="tertiary"
                height="s"
                icon={<UpdateIcon />}
                ariaLabel="Neu generieren"
                disabled={isPublic}
                onClick={() => setClientSecret(genSecret())}
              />
            }
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </div>

        <div className={css.grid2}>
          <Select
            name="authMethod"
            label="Token endpoint auth method"
            options={AUTH_METHOD_OPTIONS}
            value={AUTH_METHOD_OPTIONS.find((o) => o.value === authMethod) ?? null}
            isSearchable={false}
            onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) => {
              const v = String((opt as SelectOptionType).value);
              const wasPublic = authMethod === "none";
              setAuthMethod(v);
              if (v === "none") {
                setClientSecret("");
              } else if (wasPublic && !clientSecret) {
                // Only a public→confidential switch gains a secret. Between two
                // confidential methods the client keeps the one it already has —
                // which is masked, so an empty field must not imply "generate".
                setClientSecret(genSecret());
              }
            }}
          />
          <Input
            name="idTokenAlg"
            label="ID-Token Signaturalgorithmus"
            placeholder="z. B. RS256, HS256"
            value={idTokenAlg}
            onChange={(e) => setIdTokenAlg(e.target.value)}
          />
        </div>

        <div className={css.divider} />

        <Select
          name="grantTypes"
          label="Grant Types"
          isMulti
          isClearable={false}
          isSearchable={false}
          options={GRANT_TYPE_SELECT}
          value={toSelected(GRANT_TYPE_SELECT, grantTypes)}
          onChange={(e) => setGrantTypes(toValues(e))}
        />
        <Select
          name="responseTypes"
          label="Response types"
          isMulti
          isClearable={false}
          isSearchable={false}
          options={RESPONSE_TYPE_SELECT}
          value={toSelected(RESPONSE_TYPE_SELECT, responseTypes)}
          onChange={(e) => setResponseTypes(toValues(e))}
        />
        <div className={css.fld}>
          <Select
            name="scopes"
            label="Scope"
            isMulti
            isClearable={false}
            options={SCOPE_SELECT}
            value={toSelected(SCOPE_SELECT, scopes)}
            onChange={(e) => {
              // openid is mandatory — re-add it if the admin removes it.
              const next = toValues(e);
              setScopes(next.includes("openid") ? next : ["openid", ...next]);
            }}
          />
          <span className={css.hint}>
            Gespeichert als leerzeichengetrennter String.{" "}
            <code className={css.fchipMono}>openid</code> ist verpflichtend.
          </span>
        </div>

        <div className={css.divider} />

        <UriEditor
          label="Redirect URIs"
          hint="Erlaubte Rücksprung-Adressen nach erfolgreicher Anmeldung."
          icon={
            <ExternalLinkIcon
              style={{ width: 15, height: 15, color: "var(--color-grey-light)", flexShrink: 0 }}
            />
          }
          placeholder="https://app.example.de/auth/callback"
          value={redirectUris}
          onChange={setRedirectUris}
        />
        <UriEditor
          label="Post-Logout Redirect URIs"
          hint="Optional — Rücksprung nach der Abmeldung."
          icon={
            <LogOutIcon
              style={{ width: 15, height: 15, color: "var(--color-grey-light)", flexShrink: 0 }}
            />
          }
          placeholder="https://app.example.de/"
          value={postLogout}
          onChange={setPostLogout}
        />

        <div className={css.divider} />

        <Input
          name="logoUri"
          label="Logo-URL"
          placeholder="https://…/logo.svg"
          value={logoUri}
          onChange={(e) => setLogoUri(e.target.value)}
        />
        <Input
          name="tosUri"
          label="Nutzungsbedingungen-URL"
          placeholder="https://…"
          value={tosUri}
          onChange={(e) => setTosUri(e.target.value)}
        />
        <Input
          name="policyUri"
          label="Datenschutz-URL"
          placeholder="https://…"
          value={policyUri}
          onChange={(e) => setPolicyUri(e.target.value)}
        />

        <div className={css.divider} />

        <div className={css.fld}>
          <label className={css.label}>TKS-Erweiterungen</label>
          <div className={css.stackSm}>
            <Toggle
              checked={pkceOptional}
              onToggle={setPkceOptional}
              title="PKCE optional (TKS)"
              sub="Hebt den PKCE-Zwang für diesen Client auf."
            />
            <Toggle
              checked={consentOptional}
              onToggle={setConsentOptional}
              title="Einwilligung optional (TKS)"
              sub="Überspringt den Consent-Screen (First-Party-Apps)."
            />
            <Toggle
              checked={licenseOptional}
              onToggle={setLicenseOptional}
              title="Lizenzprüfung optional (TKS)"
              sub="Token-Ausgabe ohne aktive Produktlizenz."
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
